import { getMediaBunny } from 'utils/mediabunny'

/**
 * Extracts a single frame from a video at a specific timestamp using mediabunny.
 * Used by the FrameExtractButton feature to capture a video frame as an image.
 * @param url - Video URL
 * @param timestamp - Timestamp in seconds
 * @returns Blob of the extracted frame
 */
export const extractFrameAtTimestamp = async (
  url: string,
  timestamp: number
): Promise<Blob> => {
  const MediaBunny = await getMediaBunny()
  const { Input, ALL_FORMATS, UrlSource, CanvasSink } = MediaBunny
  const input = new Input({
    source: new UrlSource(url),
    formats: ALL_FORMATS
  })
  const videoTrack = await input.getPrimaryVideoTrack()
  if (!videoTrack) {
    throw new Error('File has no video track.')
  }
  if (videoTrack.codec === null) {
    throw new Error('Unsupported video codec.')
  }

  if (!(await videoTrack.canDecode())) {
    throw new Error('Unable to decode the video track.')
  }

  const durationSeconds = await videoTrack.computeDuration()
  const firstTimestamp = await videoTrack.getFirstTimestamp()

  const clampedTimestamp = Math.max(
    firstTimestamp,
    Math.min(timestamp, durationSeconds)
  )

  const { width, height } = {
    width: videoTrack.displayWidth,
    height: videoTrack.displayHeight
  }

  const sink = new CanvasSink(videoTrack, {
    width: Math.floor(width * window.devicePixelRatio),
    height: Math.floor(height * window.devicePixelRatio),
    fit: 'fill'
  })

  const frames = sink.canvasesAtTimestamps([clampedTimestamp])
  const wrappedCanvas = await frames.next()

  if (!wrappedCanvas.value?.canvas) {
    throw new Error('Failed to extract frame at timestamp.')
  }

  const canvas = wrappedCanvas.value.canvas as HTMLCanvasElement
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      b => (b ? resolve(b) : reject(new Error('Failed to get blob'))),
      'image/jpeg'
    )
  })
  canvas.remove()

  return blob
}
